/**
 * Workshop version of MusicFrame — Spotify official embed player.
 *
 * WORKSHOP EXCEPTION: this renders Spotify's ACTUAL native player UI via the
 * official oEmbed iframe (https://open.spotify.com/embed/track/{id}, per
 * Spotify developer docs) — an explicit user-demanded exception to the
 * no-third-party rule (no npm dep needed). The official embed needs network
 * access; production should handle offline with the previous minimal
 * now-playing row as fallback.
 *
 * Deliberately no player chrome of our own: hover pill only, no
 * FrameHeader/FrameFooter.
 */

import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui";
import { Spinner } from "@/components/ui/spinner";
import type { FrameStatus } from "@/workshop/components/shapes/shared/frame-utils";

// ─── Types ────────────────────────────────────────────────────────────────────

interface MusicFrameProps {
  track?: string;
  artist?: string;
  album?: string;
  position?: number;
  duration?: number;
  context?: string;
  embedId?: string;
  status?: FrameStatus;
  className?: string;
  "data-testid"?: string;
}

// ─── Main component ───────────────────────────────────────────────────────────

export function MusicFrame({
  track = "Never Gonna Give You Up",
  artist = "Rick Astley",
  embedId = "4uLU6hMCjMI75M1A2tKUQ",
  status = "done",
  className,
  "data-testid": testId = "music-frame",
}: MusicFrameProps) {
  return (
    <div
      className={cn(surfaceRoleClassName("frame"), "group relative flex flex-col overflow-hidden", className)}
      {...surfaceRoleAttributes("frame")}
      data-testid={testId}
    >
      <div className="flex flex-1 flex-col gap-2 px-3 py-2.5">
        {status === "error" ? (
          <div className="flex items-center gap-2 py-3" data-testid={`${testId}-error`}>
            <Icons.CircleAlert className="h-3.5 w-3.5 shrink-0 text-status-error" aria-hidden="true" />
            <span className="text-canvas-base text-muted">Couldn&apos;t load track. Try again.</span>
          </div>
        ) : status === "streaming" ? (
          <div className="flex items-center gap-2 py-3" data-testid={`${testId}-loading`}>
            <Spinner className="h-3 w-3" />
            <span className="text-canvas-base text-muted">Finding the right track…</span>
          </div>
        ) : !track ? (
          <div className="flex items-center gap-2 py-3" data-testid={`${testId}-empty`}>
            <span className="text-canvas-base text-muted">No track selected.</span>
          </div>
        ) : (
          // PRODUCTION RULE: an iframe is a black box — its internals can't be
          // themed and it swallows all pointer events, so a production React
          // Flow node can never be dragged from inside the player. Node drag
          // must start from the frame border / hover-pill area; when porting
          // to production keep the hover chrome (drag handle) OUTSIDE this
          // iframe box.
          <div className="flex min-h-0 flex-1 flex-col gap-2">
            <div className="flex min-w-0 shrink-0 items-center gap-2.5">
              <div
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-preview bg-glass-fill-dense"
                aria-hidden="true"
              >
                <Icons.Play className="h-3.5 w-3.5 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-canvas-sm font-semibold text-primary">
                  {track}
                </p>
                <p className="truncate text-canvas-xs text-muted">
                  {artist}
                </p>
              </div>
            </div>
            <div
              className="nodrag min-h-0 flex-1 overflow-hidden rounded-2xl border border-glass-stroke"
              data-testid={`${testId}-embed-wrap`}
            >
              <iframe
                src={`https://open.spotify.com/embed/track/${embedId}?theme=0`}
                title="Spotify player"
                aria-label={`Spotify player: ${track} by ${artist}`}
                loading="lazy"
                allow="encrypted-media"
                data-testid={`${testId}-embed`}
                className="h-full min-h-[160px] w-full border-0"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export type { MusicFrameProps };
